import React, { useEffect, useState } from 'react';
import { authToken } from '../../authToken';
import { Line } from 'react-chartjs-2';
import { Chart, LineElement, CategoryScale, LinearScale, PointElement, TimeScale, Tooltip } from 'chart.js';
import 'chartjs-adapter-date-fns';
import CloseButton from '../../components/CloseButton';
import BackButton from '../../components/BackButton';
import SugarAddPanel from './SugarAddPanel';
import InsulinAddPanel from './InsulinAddPanel';
import BaseAddPanel from './BaseAddPanel';
import VortioxetineAddPanel from '../other/VortioxetineAddPanel';
import { API_BASE_URL } from '../../config';

Chart.register(LineElement, CategoryScale, LinearScale, PointElement, TimeScale, Tooltip);

//const ENDPOINT_HOST = "https://zenit.poznan.pl:4040";
//const ENDPOINT_SUGAR = `${ENDPOINT_HOST}/sugar_24h`;
//const ENDPOINT_NOVORAPID = `${ENDPOINT_HOST}/novorapid`;
//const ENDPOINT_TRESIBA = `${ENDPOINT_HOST}/tresiba`;

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_SUGAR = `${ENDPOINT_HOST}/sugar`;
const ENDPOINT_NOVORAPID = `${ENDPOINT_HOST}/insulin`;
const ENDPOINT_TRESIBA = `${ENDPOINT_HOST}/base`;

// --- BACKGROUND BANDS PLUGIN ---
const bgBandsPlugin = {
  id: 'bgBands',
  beforeDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    const y = scales.y;
    const bands = [
      { from: 50, to: 80, color: '#FFDDDD' },
      { from: 80, to: 100, color: '#FFFFDD' },
      { from: 100, to: 180, color: '#DDFFDD' },
      { from: 180, to: 220, color: '#FFFFDD' },
      { from: 220, to: y.max, color: '#FFDDDD' },
    ];
    bands.forEach(band => {
      let y1 = y.getPixelForValue(Math.min(band.to, y.max));
      let y2 = y.getPixelForValue(Math.max(band.from, 50));
      if (y1 > y2) [y1, y2] = [y2, y1];
      ctx.save();
      ctx.fillStyle = band.color;
      ctx.fillRect(chartArea.left, y1, chartArea.right - chartArea.left, y2 - y1);
      ctx.restore();
    });
  }
};

function getHourStart(ts) {
  const d = new Date(ts);
  d.setMinutes(0, 0, 0);
  return d.getTime();
} 

// Color logic for progress bars
function getBarColor(type, percent, mins) {
  if (type === 'novorapid') {
    if (mins <= 60) return '#36a2eb'; // blue
    if (mins <= 180) return '#43ea5e'; // green
    if (mins <= 240) return '#f6e96b'; // yellow
    return '#ea4343'; // red
  }
  if (type === 'tresiba') {
    if (mins <= 180) return '#36a2eb'; // blue
    if (mins <= 720) return '#43ea5e'; // green
    if (mins <= 780) return '#f6e96b'; // yellow
    return '#ea4343'; // red
  }
  return '#ccc';
}

// Progress bar component
function ProgressBar({ label, percent, timeStr, color }) {
  return (
    <div style={{ margin: '8px 0', width: '100%' }}>
      <div style={{
        fontSize: '1.08em',
        marginBottom: 3,
        display: 'flex',
        justifyContent: 'space-between',
        color: '#222',
        opacity: 0.8
      }}>
        <span>{label}</span>
        <span>{percent}%</span>
      </div>
      <div style={{
        background: '#eee',
        borderRadius: 8,
        height: 18,
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percent}%`,
          background: color,
          height: '100%',
          transition: 'width 0.4s, background 0.4s',
        }}/>
      </div>
      {timeStr && (
        <div style={{ fontSize: '0.95em', color: '#888', marginTop: 2, textAlign: 'right' }}>
          {timeStr}
        </div>
      )}
    </div>
  );
}



export default function DiabetesPanel({ onClose }) {
  const [chartData, setChartData] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Progress bar state
  const [novorapid, setNovorapid] = useState(null);
  const [tresiba, setTresiba] = useState(null);

  // Panel state - tracks which sub-panel is open
  const [activePanel, setActivePanel] = useState(null);

  // Responsive sizing
  const [panelSize, setPanelSize] = useState({ width: 420 });
  useEffect(() => {
    function handleResize() {
      const w = Math.min(window.innerWidth - 32, 520);
      setPanelSize({
        width: Math.max(260, w)
      });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data fetching functions
  const refreshInsulinData = () => {
    fetch(ENDPOINT_NOVORAPID, {
      headers: {
  ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => res.json())
      .then(data => setNovorapid(data))
      .catch(() => setNovorapid(null));
  };

  const refreshBaseData = () => {
    fetch(ENDPOINT_TRESIBA, {
      headers: {
  ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => res.json())
      .then(data => setTresiba(data))
      .catch(() => setTresiba(null));
  };

  // Fetch Novorapid & Tresiba
  const refreshSugarData = () => {
    setLoading(true);
    fetch(ENDPOINT_SUGAR, {
      headers: {
  ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => res.json())
      .then(events => {
        const now = Date.now();
        const start = now - 24 * 60 * 60 * 1000;
        const dataPoints = events
          .filter(e => e.val > 0 && e.timestamp >= start && e.timestamp <= now)
          .map(e => ({
            x: e.timestamp,
            y: e.val
          }))
          .sort((a, b) => a.x - b.x);
        // --- Y axis: min always 50, max rounded up to next 50 + 50 ---
        const yValues = dataPoints.map(pt => pt.y).filter(y => y !== null && y !== undefined);
        let maxY = 250;
        if (yValues.length) {
          const maxVal = Math.max(...yValues);
          maxY = Math.ceil(maxVal / 50) * 50 + 50;
          if (maxY < 100) maxY = 100;
        }
        setChartData({
          datasets: [
            {
              label: 'Glucose (mg/dL)',
              data: dataPoints,
              borderColor: '#36a2eb',
              backgroundColor: '#222',
              borderWidth: 2,
              tension: 0.5,
              pointRadius: 4,
              spanGaps: true,
              showLine: true,
              fill: false,
            }
          ]
        });
        setOptions({
          responsive: true,
          maintainAspectRatio: false,
          plugins: { 
            bgBands: true,
            tooltip: { mode: 'index', intersect: false }
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'hour',
                tooltipFormat: 'dd.MM HH:mm',
                displayFormats: { hour: 'HH:mm' },
              },
              min: (() => {
                const d = new Date();
                d.setMinutes(0, 0, 0);
                d.setHours(d.getHours() - 24);
                return d.getTime();
              })(),
              max: (() => {
                return Date.now();
              })(),
              title: { display: true, text: 'Time (Last 24h)' }
            },
            y: {
              beginAtZero: false,
              min: 50,         // Always starts at 50!
              max: maxY,
              title: { display: true, text: 'mg/dL' }
            }
          }
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    refreshInsulinData();
    refreshBaseData();
    refreshSugarData();
  }, []);

  // Progress calculations
  const now = Date.now();

  // Novorapid: latest timestamp in array, 4h bar
  let novorapidPercent = 0, novorapidStr = '', novorapidMins = 0;
  if (Array.isArray(novorapid) && novorapid.length > 0) {
    const latest = novorapid.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
    const diff = now - latest.timestamp;
    novorapidMins = Math.floor(diff / 60000);
    novorapidPercent = Math.min(100, Math.round((diff / (4 * 60 * 60 * 1000)) * 100));
    novorapidStr = novorapidMins < 0 ? '' : `${Math.floor(novorapidMins / 60)}h ${novorapidMins % 60}min ago`;
  } else if (novorapid && novorapid.timestamp) {
    const diff = now - novorapid.timestamp;
    novorapidMins = Math.floor(diff / 60000);
    novorapidPercent = Math.min(100, Math.round((diff / (4 * 60 * 60 * 1000)) * 100));
    novorapidStr = novorapidMins < 0 ? '' : `${Math.floor(novorapidMins / 60)}h ${novorapidMins % 60}min ago`;
  }

  // Tresiba: pick latest timestamp from array, 12h bar
  let tresibaPercent = 0, tresibaStr = '', tresibaMins = 0;
  if (Array.isArray(tresiba) && tresiba.length > 0) {
    const latest = tresiba.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
    const diff = now - latest.timestamp;
    tresibaMins = Math.floor(diff / 60000);
    tresibaPercent = Math.min(100, Math.round((diff / (12 * 60 * 60 * 1000)) * 100));
    tresibaStr = tresibaMins < 0 ? '' : `${Math.floor(tresibaMins / 60)}h ${tresibaMins % 60}min ago`;
  } else if (tresiba && tresiba.timestamp) {
    const diff = now - tresiba.timestamp;
    tresibaMins = Math.floor(diff / 60000);
    tresibaPercent = Math.min(100, Math.round((diff / (12 * 60 * 60 * 1000)) * 100));
    tresibaStr = tresibaMins < 0 ? '' : `${Math.floor(tresibaMins / 60)}h ${Math.floor(tresibaMins % 60)}min ago`;
  }

  // Dynamic panel height: fits content, up to 96vh
  let contentHeight = 0;
  if (activePanel) {
    // Sub-panel mode: give more space for TimePicker and other components
    contentHeight = Math.min(650, window.innerHeight * 0.85);
  } else if (!loading && chartData && options) {
    // Main dashboard: Chart: 240px, 2 bars: 54px each, buttons: 160px, header: 38px, space: 44px
    contentHeight = 38 + 240 + 54 * 2 + 160 + 44;
  } else {
    contentHeight = 360;
  }
  contentHeight = Math.min(contentHeight, window.innerHeight * 0.96);

  return (
    <div
      style={{
        background: '#fff',
        padding: '24px 12px 18px 12px',
        borderRadius: 16,
        boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        minWidth: 0,
        width: `${panelSize.width}px`,
        maxWidth: '98vw',
        minHeight: 0,
        height: `${Math.round(contentHeight)}px`,
        maxHeight: '96vh',
        overflow: 'auto',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10001
        }}
      />

      {/* BackButton - only show when in a sub-panel */}
      {activePanel && (
        <BackButton
          onClick={() => setActivePanel(null)}
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 10001
          }}
        />
      )}

      {/* Conditional content based on active panel */}
      {!activePanel ? (
        <>
          <h2 style={{ marginBottom: 18 }}>💉 Diabetes Tracker</h2>
          {loading && <div>Loading... ⏳</div>}
          {!loading && chartData && options && (
            <div style={{ height: 240, width: '100%', marginBottom: 16 }}>
              <Line data={chartData} options={options} plugins={[bgBandsPlugin]} />
            </div>
          )}
          <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
            <ProgressBar
              label="Novorapid"
              percent={novorapidPercent}
              color={getBarColor('novorapid', novorapidPercent, novorapidMins)}
              timeStr={novorapidStr}
            />
            <ProgressBar
              label="Tresiba"
              percent={tresibaPercent}
              color={getBarColor('tresiba', tresibaPercent, tresibaMins)}
              timeStr={tresibaStr}
            />
          </div>

          {/* 2x3 Button Grid */}
          <div style={{
            width: '100%', 
            maxWidth: 420, 
            margin: '20px auto 0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: '12px',
            padding: '0 8px'
          }}>
            <button
              style={{
                padding: '12px 16px',
                backgroundColor: '#36a2eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2591d9'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#36a2eb'}
              onClick={() => setActivePanel('sugar-add')}
            >
               🩸 Sugar Add
            </button>
            <button
              style={{
                padding: '12px 16px',
                backgroundColor: '#43ea5e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#3ad654'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#43ea5e'}
              onClick={() => setActivePanel('insulin-add')}
            >
               💉 Insulin Add
            </button>            
            <button
              style={{
                padding: '12px 16px',
                backgroundColor: '#f6e96b',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f4e456'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f6e96b'}
              onClick={() => setActivePanel('base-add')}
            >
               🏠 Base Add
            </button>            
          </div>

          {!loading && (!chartData || chartData.datasets[0].data.every(d => !d.y)) && (            
            <div style={{ marginTop: 24, color: "#999" }}>No data for last 24h. Come back for a taste later. 609</div>
          )}
        </>
      ) : (
        <>
          {/* Render the active sub-panel */}
          {activePanel === 'sugar-add' && <SugarAddPanel onClose={() => { refreshSugarData(); setActivePanel(null); }} />}
          {activePanel === 'insulin-add' && <InsulinAddPanel onClose={() => { refreshInsulinData(); setActivePanel(null); }} />}
          {activePanel === 'base-add' && <BaseAddPanel onClose={() => { refreshBaseData(); setActivePanel(null); }} />}
          {activePanel === 'vortioxetine-add' && <VortioxetineAddPanel onClose={() => setActivePanel(null)} />}
        </>
      )}
    </div>
  );
}
